/**
 * Double-tap fire recogniser (Bible §3 variant B). Pure: no DOM, testable in Node.
 *
 * Decides on the SECOND pointerdown, not on pointerup: mobile browsers run their own double-tap
 * (zoom) heuristics and may delay or swallow the second tap's pointerup, but the pointerdown
 * arrives. A "tap" is a pointer that went up without moving past `movePx` and within `tapMaxMs`.
 */
export interface TapFireOptions {
  /** Max time from the first tap's pointerdown to the second pointerdown. */
  windowMs: number;
  /** Max distance between the two pointerdowns. A thumb is not a mouse. */
  distancePx: number;
  /** Movement beyond this turns a tap into a swipe (never a tap). */
  movePx: number;
  /** A press held longer than this is not a tap. */
  tapMaxMs: number;
}

export const TAP_FIRE_DEFAULTS: TapFireOptions = { windowMs: 300, distancePx: 32, movePx: 18, tapMaxMs: 300 };

interface Live {
  x0: number;
  y0: number;
  t0: number;
  moved: boolean;
}

export class TapFireRecognizer {
  private readonly o: TapFireOptions;
  private live = new Map<number, Live>();
  /** The most recent completed tap, if any. */
  private lastTap: { x: number; y: number; t: number } | null = null;

  constructor(opts: Partial<TapFireOptions> = {}) {
    this.o = { ...TAP_FIRE_DEFAULTS, ...opts };
  }

  /**
   * Pointerdown. When this press is the second tap of a double-tap, returns the timestamp of the
   * pair's FIRST tap (fire now; the caller uses the time for the accidental-fire heuristic).
   * Returns null otherwise.
   */
  down(id: number, x: number, y: number, t: number): number | null {
    this.live.set(id, { x0: x, y0: y, t0: t, moved: false });
    const lt = this.lastTap;
    if (lt && t - lt.t <= this.o.windowMs && Math.hypot(x - lt.x, y - lt.y) <= this.o.distancePx) {
      this.lastTap = null; // consume: a third tap starts a fresh pair
      this.live.delete(id); // the firing press is not itself a tap candidate
      return lt.t;
    }
    return null;
  }

  move(id: number, x: number, y: number): void {
    const l = this.live.get(id);
    if (l && !l.moved && Math.max(Math.abs(x - l.x0), Math.abs(y - l.y0)) >= this.o.movePx) l.moved = true;
  }

  /** Pointer released (or cancelled). Records a tap when it qualified. */
  up(id: number, t: number): void {
    const l = this.live.get(id);
    this.live.delete(id);
    if (!l) return;
    if (l.moved || t - l.t0 > this.o.tapMaxMs) {
      this.lastTap = null; // a swipe or hold breaks any pending pair
      return;
    }
    this.lastTap = { x: l.x0, y: l.y0, t: l.t0 };
  }

  reset(): void {
    this.live.clear();
    this.lastTap = null;
  }
}
